import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { API_BASE_URL } from "../config";
import {
  Users, Layers, MoreVertical, Search, Sparkles,
  Grid, Mic, TrendingUp, TrendingDown, ArrowUpRight,
  Activity, Eye
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from "recharts";

/* ─── Gradient stat card ─── */
function StatCard({ title, value, icon: Icon, gradient, trend, trendUp, onClick }) {
  return (
    <div
      onClick={onClick}
      className="relative overflow-hidden rounded-2xl p-6 cursor-pointer group transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
      style={{ background: gradient }}
    >
      {/* Decorative circle */}
      <div className="absolute -right-4 -top-4 w-28 h-28 rounded-full bg-white/10 group-hover:scale-125 transition-transform duration-500" />
      <div className="absolute -right-8 -bottom-8 w-24 h-24 rounded-full bg-white/5" />

      <div className="relative z-10 flex items-start justify-between">
        <div>
          <p className="text-white/80 text-sm font-medium mb-2">{title}</p>
          <h2 className="text-4xl font-bold text-white tracking-tight">{value}</h2>
          {trend && (
            <div className="mt-3 flex items-center gap-1">
              {trendUp
                ? <TrendingUp size={14} className="text-white/90" />
                : <TrendingDown size={14} className="text-white/90" />}
              <span className="text-white/90 text-xs font-semibold">{trend}</span>
              <span className="text-white/60 text-xs">vs last month</span>
            </div>
          )}
        </div>
        <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center group-hover:bg-white/30 transition-all duration-300 group-hover:scale-110">
          <Icon size={22} className="text-white" />
        </div>
      </div>
    </div>
  );
}

/* ─── Custom Tooltip for charts ─── */
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl px-4 py-3 shadow-xl">
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} className="text-sm font-bold" style={{ color: p.color }}>{p.name}: {p.value}</p>
        ))}
      </div>
    );
  }
  return null;
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [data, setData] = useState({
    total_users: 0,
    total_categories: 0,
    total_subcategories: 0,
    total_posts: 0,
    total_templates: 0,
    total_voices: 0,
    total_daily_content: 0,
    total_subscriptions: 0,
  });
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [pieData, setPieData] = useState([]);
  const [recentUserSearchTerm, setRecentUserSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fCount = data.total_subcategories || 0;
    const dCount = data.total_daily_content || 0;
    const sCount = data.total_templates || 0;
    const total = fCount + dCount + sCount || 1;
    setPieData([
      { name: "Subcategories", value: fCount, color: "#6366f1", percentage: Math.round((fCount / total) * 100) },
      { name: "Daily Content", value: dCount, color: "#8b5cf6", percentage: Math.round((dCount / total) * 100) },
      { name: "Templates", value: sCount, color: "#a78bfa", percentage: Math.round((sCount / total) * 100) },
    ]);
  }, [data]);

  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE_URL}/api/admin/dashboard`).then((r) => { if (!r.ok) throw new Error("dashboard"); return r.json(); }),
      fetch(`${API_BASE_URL}/api/categories/all-categories`).then((r) => { if (!r.ok) throw new Error("categories"); return r.json(); }),
      fetch(`${API_BASE_URL}/api/users/all-users`).then((r) => { if (!r.ok) throw new Error("users"); return r.json(); }),
      fetch(`${API_BASE_URL}/api/admin/analytics`).then((r) => { if (!r.ok) throw new Error("analytics"); return r.json(); }),
    ])
      .then(([dashboardRes, categoriesRes, usersRes, analyticsRes]) => {
        if (dashboardRes?.status && dashboardRes?.data) setData(dashboardRes.data);
        else throw new Error(dashboardRes.message || "Invalid dashboard response");
        if (categoriesRes?.status && categoriesRes?.data) setCategories(categoriesRes.data);
        if (usersRes?.status && usersRes?.data) setUsers(usersRes.data);
        if (analyticsRes?.status && analyticsRes?.data) setChartData(analyticsRes.data.revenueData || []);
        setLoading(false);
      })
      .catch((err) => { setError(err.message); setLoading(false); });
  }, []);

  const getInitials = (name) =>
    name ? name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) : "?";

  const todayUsers = users.filter((u) => {
    if (!u.created_at) return false;
    return new Date(u.created_at).toLocaleDateString("en-GB") === new Date().toLocaleDateString("en-GB");
  });

  const stats = [
    { title: "Total Users", value: loading ? "—" : data.total_users.toLocaleString(), icon: Users, gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", trend: "12%", trendUp: true, path: "/users" },
    { title: "Total Categories", value: loading ? "—" : data.total_categories.toLocaleString(), icon: Layers, gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)", trend: "8.5%", trendUp: true, path: "/categories" },
    { title: "Total Subcategories", value: loading ? "—" : data.total_subcategories.toLocaleString(), icon: Grid, gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)", trend: "24%", trendUp: true, path: "/sub-category" },
    { title: "Total Subscriptions", value: loading ? "—" : data.total_subscriptions.toLocaleString(), icon: Activity, gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)", trend: "12%", trendUp: true, path: "/users" },
    { title: "Total Templates", value: loading ? "—" : data.total_templates.toLocaleString(), icon: Sparkles, gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)", trend: "18%", trendUp: true, path: "/template-management" },
    { title: "Total Voices", value: loading ? "—" : data.total_voices.toLocaleString(), icon: Mic, gradient: "linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)", trend: "7%", trendUp: true, path: "/banner-management" },
  ];

  return (
    <div className="flex bg-[#f1f5f9] dark:bg-slate-950 min-h-screen font-sans text-slate-800 dark:text-slate-100">
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen overflow-y-auto">
        <Header
          onMenuClick={() => setIsSidebarOpen(true)}
          title="Dashboard"
          subtitle="Welcome back! Here's what's happening today."
        />

        <div className="p-4 sm:p-6 lg:p-8 flex-1 w-full pb-24 space-y-8">

          {/* Error Banner */}
          {error && (
            <div className="p-4 bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-800 rounded-2xl text-rose-600 dark:text-rose-400 text-sm font-medium flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse inline-block" />
              Error loading dashboard data: {error}. Please try again.
            </div>
          )}

          {/* ── Stat Cards ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
            {stats.map((item, i) => (
              <StatCard key={i} {...item} onClick={() => item.path && navigate(item.path)} />
            ))}
          </div>

          {/* ── Charts Row ── */}
          <div className="grid lg:grid-cols-3 gap-6">

            {/* Area Chart */}
            <div className="lg:col-span-2 bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
                <div>
                  <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">User Growth</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">New users registered over time</p>
                </div>
                <select className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-300 rounded-xl px-3 py-2 outline-none focus:border-indigo-400 cursor-pointer">
                  <option>This Week</option>
                  <option>This Month</option>
                  <option>This Year</option>
                </select>
              </div>
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gradUsers" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: "#94a3b8", fontSize: 12 }} dy={8} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: "#94a3b8", fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#c7d2fe", strokeWidth: 2, strokeDasharray: "4 4" }} />
                    <Area type="monotone" dataKey="users" name="Users" stroke="#6366f1" strokeWidth={3} fillOpacity={1} fill="url(#gradUsers)" dot={false} activeDot={{ r: 6, fill: "#6366f1", strokeWidth: 0 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Donut Chart */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
              <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">Content Breakdown</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5 mb-4">Distribution by type</p>
              <div className="h-44 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData.length ? pieData : [{ name: "No Data", value: 1, color: "#e2e8f0" }]}
                      innerRadius={52}
                      outerRadius={72}
                      paddingAngle={4}
                      dataKey="value"
                      stroke="none"
                    >
                      {(pieData.length ? pieData : [{ name: "No Data", value: 1, color: "#e2e8f0" }]).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-3 mt-2">
                {pieData.map((item, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                      <span className="text-sm text-slate-600 dark:text-slate-300">{item.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${item.percentage}%`, backgroundColor: item.color }} />
                      </div>
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-200 w-10 text-right">{item.percentage}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Tables Row ── */}
          <div className="grid lg:grid-cols-2 gap-6">

            {/* Recent Users Today */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
              <div className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h2 className="text-base font-bold text-slate-800 dark:text-slate-100">Today's New Users</h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Registered today</p>
                </div>
                <div className="relative">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={recentUserSearchTerm}
                    onChange={(e) => setRecentUserSearchTerm(e.target.value)}
                    className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl pl-9 pr-4 py-2 text-sm dark:text-slate-200 outline-none focus:border-indigo-400 transition-all w-full sm:w-40"
                  />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-slate-50 dark:bg-slate-800/60">
                      <th className="px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">User</th>
                      <th className="px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-right">View</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {loading ? (
                      [1, 2, 3].map((i) => (
                        <tr key={i}>
                          <td colSpan="3" className="px-6 py-4">
                            <div className="h-4 bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />
                          </td>
                        </tr>
                      ))
                    ) : todayUsers.length === 0 ? (
                      <tr>
                        <td colSpan="3" className="px-6 py-10 text-center">
                          <div className="flex flex-col items-center gap-2">
                            <Users size={32} className="text-slate-300 dark:text-slate-600" />
                            <p className="text-sm text-slate-400">No users registered today</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      todayUsers
                        .filter((u) =>
                          (u.fullname || "").toLowerCase().includes(recentUserSearchTerm.toLowerCase()) ||
                          (u.email || "").toLowerCase().includes(recentUserSearchTerm.toLowerCase())
                        )
                        .slice(0, 5)
                        .map((user, i) => (
                          <tr key={user.user_id_PK || i} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                  {getInitials(user.fullname)}
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{user.fullname || "N/A"}</p>
                                  <p className="text-xs text-slate-500 dark:text-slate-400">{user.mobile_number || "N/A"}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${user.status === "Premium"
                                ? "bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400"
                                : "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${user.status === "Premium" ? "bg-amber-500" : "bg-emerald-500"}`} />
                                {user.status || "Free"}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <button
                                onClick={() => navigate(`/users/${user.user_id_PK}`)}
                                className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
                              >
                                <Eye size={14} /> View
                              </button>
                            </td>
                          </tr>
                        ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Categories Table */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
              <div className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h2 className="text-base font-bold text-slate-800 dark:text-slate-100">Categories</h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Overview of all categories</p>
                </div>
                <button
                  onClick={() => navigate("/categories")}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 bg-indigo-50 dark:bg-indigo-500/10 px-3 py-2 rounded-xl transition-colors"
                >
                  View All <ArrowUpRight size={14} />
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-slate-50 dark:bg-slate-800/60">
                      <th className="px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Category</th>
                      <th className="px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {loading ? (
                      [1, 2, 3].map((i) => (
                        <tr key={i}>
                          <td colSpan="3" className="px-6 py-4">
                            <div className="h-4 bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />
                          </td>
                        </tr>
                      ))
                    ) : categories.length === 0 ? (
                      <tr>
                        <td colSpan="3" className="px-6 py-10 text-center">
                          <div className="flex flex-col items-center gap-2">
                            <Layers size={32} className="text-slate-300 dark:text-slate-600" />
                            <p className="text-sm text-slate-400">No categories found</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      categories.slice(0, 5).map((item, i) => {
                        const n = item.category_name.toLowerCase();
                        let type = "Template", colorClass = "bg-purple-50 text-purple-600 dark:bg-purple-500/10 dark:text-purple-400";
                        if (n.includes("festival")) { type = "Festival"; colorClass = "bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400"; }
                        else if (n.includes("darshan")) { type = "Darshan"; colorClass = "bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400"; }
                        else if (n.includes("suvichar") || n.includes("quote")) { type = "Suvichar"; colorClass = "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400"; }
                        return (
                          <tr key={item.category_id_PK || i} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 flex-shrink-0 bg-slate-100 dark:bg-slate-800">
                                  <img src={item.category_image} className="w-full h-full object-cover" alt={item.category_name} />
                                </div>
                                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 line-clamp-1">{item.category_name}</p>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-semibold ${colorClass}`}>{type}</span>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <button onClick={() => navigate("/categories")} className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 transition-colors">
                                <Eye size={14} /> View
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}